import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SourceDocumentDialog } from '../../components/ChatView/message-parts/SourceDocumentDialog';
import { AskSession } from '../../components/EvidenceAsk/AskSession';
import { RfiEvidence } from '../../components/RfiEvidence/RfiEvidence';
import { useAuth } from '../../contexts/authContext';
import { OnlyOfficeConnectorProvider } from '../../contexts/OnlyOfficeConnectorContext';
import { assistConversationId } from '../../lib/chat/envelope';
import { useEntityArtifacts } from '../Capture/documentHooks';
import { RfiSectionEditor } from './RfiSectionEditor';
import { useRfiSections } from './rfiSectionHooks';
/** RFI-local navigation keeps every visited section mounted, including its cursor and dirty OO buffer. */ export function RfiAuthoringPanel({ rfiResponseProjectId, zouProjectId }) {
    const { user } = useAuth();
    const { data: sections = [] } = useRfiSections(rfiResponseProjectId);
    const sources = useEntityArtifacts('rfi_response_project', rfiResponseProjectId);
    const [params, setParams] = useSearchParams();
    const selectedId = params.get('section') || sections[0]?.id || '';
    const selected = sections.find((section)=>section.id === selectedId);
    const [visited, setVisited] = useState([]);
    const reviewExporters = useRef(new Map());
    const registerReviewExport = useCallback((sectionId, exportDocx)=>{
        reviewExporters.current.set(sectionId, exportDocx);
        return ()=>{
            if (reviewExporters.current.get(sectionId) === exportDocx) reviewExporters.current.delete(sectionId);
        };
    }, []);
    const [askVisited, setAskVisited] = useState(false);
    const panel = params.get('rfiPanel') || '';
    const source = sources.data?.find((artifact)=>artifact.artifactId === params.get('source'));
    const navigatePanel = (next)=>{
        const nextParams = new URLSearchParams(params);
        if (next) nextParams.set('rfiPanel', next);
        else nextParams.delete('rfiPanel');
        setParams(nextParams);
        if (next === 'ask') setAskVisited(true);
    };
    useEffect(()=>{
        if (selected) setVisited((ids)=>ids.includes(selected.id) ? ids : [
                ...ids,
                selected.id
            ]);
    }, [
        selected
    ]);
    useEffect(()=>{
        if (panel === 'ask') setAskVisited(true);
    }, [
        panel
    ]);
    const envelope = useMemo(()=>({
            surface: 'rfi_response',
            projectId: zouProjectId
        }), [
        zouProjectId
    ]);
    const chatId = assistConversationId(envelope, user?.id);
    if (sections.length === 0) return null;
    return /*#__PURE__*/ _jsx(OnlyOfficeConnectorProvider, {
        children: /*#__PURE__*/ _jsxs("section", {
            className: "space-y-3 rounded-md border border-border bg-card p-3",
            "data-testid": Testids.rfiEditor.panel,
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Write an RFI section",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        "aria-label": "Write an RFI section",
                        "data-testid": Testids.rfiEditor.sectionSelect,
                        value: selectedId,
                        onChange: (event)=>{
                            const next = new URLSearchParams(params);
                            next.set('section', event.target.value);
                            setParams(next);
                        },
                        children: sections.map((section)=>/*#__PURE__*/ _jsx("option", {
                                value: section.id,
                                children: section.title
                            }, section.id))
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            "data-testid": Testids.rfiEditor.openSource,
                            onClick: ()=>navigatePanel('source'),
                            children: "Open Source"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            "data-testid": Testids.rfiEditor.openAsk,
                            onClick: ()=>navigatePanel('ask'),
                            children: "Ask Specra"
                        }),
                        panel && /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            "data-testid": Testids.rfiEditor.returnToEditor,
                            onClick: ()=>navigatePanel(''),
                            children: "Return to section"
                        })
                    ]
                }),
                panel === 'source' && /*#__PURE__*/ _jsxs("aside", {
                    className: "space-y-3 rounded-md border border-border p-3",
                    "data-testid": Testids.rfiEditor.sourcePanel,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Linked RFI source documents"
                        }),
                        sources.isLoading ? /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Loading linked documents…"
                        }) : sources.isError ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    children: "Linked sources unavailable."
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    onClick: ()=>void sources.refetch(),
                                    children: "Retry sources"
                                })
                            ]
                        }) : sources.data?.length ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "aria-label": "Linked source document",
                                    "data-testid": Testids.rfiEditor.sourceSelect,
                                    value: source?.artifactId || '',
                                    onChange: (event)=>{
                                        const next = new URLSearchParams(params);
                                        next.set('source', event.target.value);
                                        setParams(next);
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: "Choose a linked source"
                                        }),
                                        sources.data.map((artifact)=>/*#__PURE__*/ _jsx("option", {
                                                value: artifact.artifactId,
                                                children: artifact.filename
                                            }, artifact.artifactId))
                                    ]
                                }),
                                source && /*#__PURE__*/ _jsxs(SpecraButton, {
                                    "data-testid": Testids.rfiEditor.sourcePreview,
                                    onClick: ()=>navigatePanel('source-preview'),
                                    children: [
                                        "Read ",
                                        source.filename
                                    ]
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(SpecraText, {
                            children: "No source documents are linked. Add a file in Documents below."
                        })
                    ]
                }),
                panel === 'source-preview' && source && /*#__PURE__*/ _jsx(SourceDocumentDialog, {
                    documentId: source.artifactId,
                    name: source.filename,
                    onClose: ()=>navigatePanel('source')
                }),
                askVisited && /*#__PURE__*/ _jsx("div", {
                    hidden: panel !== 'ask',
                    children: /*#__PURE__*/ _jsx(AskSession, {
                        chatId: chatId,
                        envelope: envelope,
                        onClose: ()=>navigatePanel(''),
                        context: {
                            workspace: 'RFI Response',
                            record: {
                                id: rfiResponseProjectId,
                                label: selected?.title || 'RFI Response'
                            },
                            version: 'Current saved RFI section; unsaved editor content is not sent automatically',
                            // Linked uploads are artifacts, not Office document IDs accepted by Ask's source guard.
                            // Keep their selection in workspace context; the source reader authorizes their content.
                            filters: params.toString(),
                            target: {
                                sectionId: selectedId,
                                questionId: params.get('question') || ''
                            }
                        }
                    })
                }),
                selected && /*#__PURE__*/ _jsx(RfiEvidence, {
                    target: {
                        rfiResponseProjectId,
                        sectionId: selectedId
                    },
                    reviewOfficeDocx: async ()=>{
                        const exportDocx = reviewExporters.current.get(selectedId);
                        if (!exportDocx) throw new Error('Office document is still opening. Retry when ready.');
                        return exportDocx();
                    },
                    questionId: params.get('question') || '',
                    onQuestionChange: (questionId)=>{
                        const next = new URLSearchParams(params);
                        next.set('question', questionId);
                        setParams(next);
                    }
                }, selectedId),
                visited.map((id)=>{
                    const section = sections.find((item)=>item.id === id);
                    return section ? /*#__PURE__*/ _jsx("div", {
                        hidden: selectedId !== id,
                        children: /*#__PURE__*/ _jsx(RfiSectionEditor, {
                            rfiResponseProjectId: rfiResponseProjectId,
                            sectionId: id,
                            title: section.title,
                            registerReviewExport: registerReviewExport
                        })
                    }, id) : null;
                })
            ]
        })
    });
}
